import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { loaderState } from 'recoils/atoms'

import { Modal, Button, Progress } from 'components'
import { IconTryAgain } from 'components/Icons'

interface DownloadProgressModalProps {
  percent: number
  loadedSize: number
  totalSize: number
  fileName: string
  isOpen: boolean
  isError: boolean
  onClose: () => void
}

const DownloadProgressModal: React.FC<DownloadProgressModalProps> = (
  props: DownloadProgressModalProps,
): JSX.Element => {
  const { t } = useTranslation('common')
  const setLoader = useSetRecoilState(loaderState)

  const { percent, fileName, isOpen, isError, onClose } = props

  const base = 'download-progress-modal'
  const isOpenModal = isOpen

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()
  }

  const renderHeader = (fileName: string) => {
    const base = 'download-progress-modal__header'
    const fileType = getFileExtension(fileName)
    const title = t('downloadTitle.label', { fileType })

    return (
      <div className={base}>
        <span className={`${base}--title`}>{title}</span>
        <span className={`${base}--subtitle`}>{fileName}</span>
      </div>
    )
  }

  const renderStatus = (isError: boolean) => {
    const base = 'download-progress-modal__status'

    if (isError) {
      return (
        <div className={`${base}--error-layout`}>
          <IconTryAgain variants='sm' />
          <span className={`${base}--error-text`}>
            {t('downloadAgain.label')}
          </span>
        </div>
      )
    }

    if (percent >= 100) {
      return (
        <span className={`${base}--success-text`}>
          {t('downloadSuccessfully.label')}
        </span>
      )
    }

    return null
  }

  useEffect(() => {
    if (isOpenModal) {
      setLoader(false)
    }
  }, [isOpenModal])

  return isOpenModal ? (
    <Modal isOpen={isOpenModal} onClose={onClose} isIcon={false}>
      <div className={`${base}__layout`}>
        {renderHeader(fileName)}
        <Progress percent={percent} />
        {renderStatus(isError)}

        <Button className={`${base}__cancel-button`} onClick={onClose}>
          {t('cancel.button')}
        </Button>
      </div>
    </Modal>
  ) : (
    <div />
  )
}

export default DownloadProgressModal
